import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download,
  Print,
  Refresh,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  Assessment,
  PieChart,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface FinancialData {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
  cashFlow: number;
}

interface ProjectFinancials {
  project: string;
  budgeted: number;
  actual: number;
  variance: number;
  completion: number;
}

interface ExpenseCategory {
  category: string;
  amount: number;
  percentage: number;
  color: string;
  [key: string]: string | number;
}

const FinancialReports: React.FC = () => {
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('last-12-months');
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [projectData, setProjectData] = useState<ProjectFinancials[]>([]);
  const [expenseData, setExpenseData] = useState<ExpenseCategory[]>([]);

  // Fetch financial data from API
  useEffect(() => {
    const fetchFinancialData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        // Fetch dashboard data
        // Map UI date range selections to backend-supported values
        const dateRangeMap: Record<string, string> = {
          'last-3-months': 'quarter',
          'last-6-months': 'quarter', // approximate
          'last-12-months': 'year',
          'ytd': 'year',
          'custom': 'month', // fallback until custom from/to supported
        };
        const apiDateRange = dateRangeMap[dateRange] || 'month';

        const dashboardResponse = await fetch(
          `/api/financial-reports/dashboard?reportType=${reportType}&dateRange=${apiDateRange}`,
          { headers }
        );
        
        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          
          // Transform dashboard data for charts
          if (Array.isArray(dashboardData?.data?.financialData)) {
            const transformedFinancialData = dashboardData.data.financialData.map((item: any) => ({
              period: item.period,
              revenue: item.revenue || 0,
              expenses: item.expenses || 0,
              profit: item.profit ?? ((item.revenue || 0) - (item.expenses || 0)),
              cashFlow: item.cashFlow || 0,
            }));
            setFinancialData(transformedFinancialData);
          } else {
            setFinancialData([]);
          }

          // Transform project data
          if (Array.isArray(dashboardData?.data?.projectData)) {
            const transformedProjectData = dashboardData.data.projectData.map((p: any) => {
              const budgetedVal = typeof p?.budgeted === 'number'
                ? p.budgeted
                : (p?.budgeted?.totalBudget ?? p?.budgeted?.budget ?? 0);
              const actualVal = typeof p?.actual === 'number'
                ? p.actual
                : (p?.actual?.actualCost ?? p?.actual?.costToDate ?? 0);
              const varianceVal = typeof p?.variance === 'number'
                ? p.variance
                : (actualVal - budgetedVal);
              return {
                project: p.project || 'Unknown Project',
                budgeted: budgetedVal,
                actual: actualVal,
                variance: varianceVal,
                completion: p.completion ?? 0,
              } as ProjectFinancials;
            });
            setProjectData(transformedProjectData);
          } else {
            setProjectData([]);
          }

          // Transform expense data
          if (Array.isArray(dashboardData?.data?.expenseData)) {
            const transformedExpenseData = dashboardData.data.expenseData.map((expense: any) => ({
              category: expense.category || 'Other',
              amount: expense.amount || 0,
              percentage: expense.percentage || 0,
              color: expense.color || '#8884d8',
            }));
            setExpenseData(transformedExpenseData);
          } else {
            setExpenseData([]);
          }
        } else {
          console.error('Failed to fetch financial dashboard data');
          // Set empty data on error
          setFinancialData([]);
          setProjectData([]);
          setExpenseData([]);
        }
      } catch (error) {
        console.error('Error fetching financial data:', error);
        // Set empty data on error
        setFinancialData([]);
        setProjectData([]);
        setExpenseData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, [reportType, dateRange]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateTotals = () => {
    // Ensure financialData is always an array for safe operations
    const financialDataArray = Array.isArray(financialData) ? financialData : [];
    
    const totalRevenue = financialDataArray.reduce((sum, item) => sum + (item?.revenue || 0), 0);
    const totalExpenses = financialDataArray.reduce((sum, item) => sum + (item?.expenses || 0), 0);
    const totalProfit = financialDataArray.reduce((sum, item) => sum + (item?.profit || 0), 0);
    const avgCashFlow = financialDataArray.length > 0 
      ? financialDataArray.reduce((sum, item) => sum + (item?.cashFlow || 0), 0) / financialDataArray.length 
      : 0;
    
    return { totalRevenue, totalExpenses, totalProfit, avgCashFlow };
  };

  const formatPercent = (value: number) => `${Number.isFinite(value) ? value.toFixed(1) : '0.0'}%`;

  const getMoMChange = (key: keyof FinancialData) => {
    const arr = Array.isArray(financialData) ? financialData : [];
    if (arr.length < 2) return { pct: 0, direction: 'neutral' as const };
    const prev = (arr[arr.length - 2]?.[key] ?? 0) as number;
    const last = (arr[arr.length - 1]?.[key] ?? 0) as number;
    if (!prev || prev <= 0) return { pct: 0, direction: 'neutral' as const };
    const delta = last - prev;
    const pct = (delta / prev) * 100;
    return { pct, direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral' };
  };

  const getMarginMoMChange = () => {
    const arr = Array.isArray(financialData) ? financialData : [];
    if (arr.length < 2) return { pct: 0, direction: 'neutral' as const };
    const prevRev = arr[arr.length - 2]?.revenue ?? 0;
    const prevProf = arr[arr.length - 2]?.profit ?? 0;
    const lastRev = arr[arr.length - 1]?.revenue ?? 0;
    const lastProf = arr[arr.length - 1]?.profit ?? 0;
    if (!prevRev || prevRev <= 0) return { pct: 0, direction: 'neutral' as const };
    const prevMargin = prevProf / prevRev;
    const lastMargin = lastRev > 0 ? (lastProf / lastRev) : 0;
    const delta = lastMargin - prevMargin;
    const pct = delta * 100;
    return { pct, direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral' };
  };

  const { totalRevenue, totalExpenses, totalProfit, avgCashFlow } = calculateTotals();
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
  const revenueChange = getMoMChange('revenue');
  const expensesChange = getMoMChange('expenses');
  const profitChange = getMoMChange('profit');
  const marginChange = getMarginMoMChange();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading financial reports...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Financial Reports
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
          <Button variant="contained" startIcon={<Refresh />}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            label="Report Type"
            onChange={(e) => setReportType(e.target.value)}
          >
            <MenuItem value="overview">Financial Overview</MenuItem>
            <MenuItem value="profit-loss">Profit & Loss</MenuItem>
            <MenuItem value="cash-flow">Cash Flow</MenuItem>
            <MenuItem value="project-analysis">Project Analysis</MenuItem>
            <MenuItem value="expense-breakdown">Expense Breakdown</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="last-3-months">Last 3 Months</MenuItem>
            <MenuItem value="last-6-months">Last 6 Months</MenuItem>
            <MenuItem value="last-12-months">Last 12 Months</MenuItem>
            <MenuItem value="ytd">Year to Date</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        {dateRange === 'custom' && (
          <>
            <TextField
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalance color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(totalRevenue)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {revenueChange.direction === 'up' && <TrendingUp color="success" fontSize="small" />}
                  {revenueChange.direction === 'down' && <TrendingDown color="error" fontSize="small" />}
                  {revenueChange.direction === 'neutral' && <TrendingUp color="disabled" fontSize="small" />}
                  <Typography variant="body2" color={revenueChange.direction === 'up' ? 'success.main' : revenueChange.direction === 'down' ? 'error.main' : 'text.secondary'}>
                    {formatPercent(Math.abs(revenueChange.pct))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Receipt color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Expenses
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(totalExpenses)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {expensesChange.direction === 'up' && <TrendingUp color="error" fontSize="small" />}
                  {expensesChange.direction === 'down' && <TrendingDown color="success" fontSize="small" />}
                  {expensesChange.direction === 'neutral' && <TrendingUp color="disabled" fontSize="small" />}
                  <Typography variant="body2" color={expensesChange.direction === 'up' ? 'error.main' : expensesChange.direction === 'down' ? 'success.main' : 'text.secondary'}>
                    {formatPercent(Math.abs(expensesChange.pct))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Assessment color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Net Profit
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(totalProfit)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {profitChange.direction === 'up' && <TrendingUp color="success" fontSize="small" />}
                  {profitChange.direction === 'down' && <TrendingDown color="error" fontSize="small" />}
                  {profitChange.direction === 'neutral' && <TrendingUp color="disabled" fontSize="small" />}
                  <Typography variant="body2" color={profitChange.direction === 'up' ? 'success.main' : profitChange.direction === 'down' ? 'error.main' : 'text.secondary'}>
                    {formatPercent(Math.abs(profitChange.pct))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PieChart color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Profit Margin
                </Typography>
                <Typography variant="h5">
                  {profitMargin.toFixed(1)}%
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {marginChange.direction === 'up' && <TrendingUp color="success" fontSize="small" />}
                  {marginChange.direction === 'down' && <TrendingDown color="error" fontSize="small" />}
                  {marginChange.direction === 'neutral' && <TrendingUp color="disabled" fontSize="small" />}
                  <Typography variant="body2" color={marginChange.direction === 'up' ? 'success.main' : marginChange.direction === 'down' ? 'error.main' : 'text.secondary'}>
                    {formatPercent(Math.abs(marginChange.pct))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Charts Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Revenue & Profit Trend */}
          <Paper sx={{ p: 3, flex: '2 1 600px', minWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Revenue & Profit Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={financialData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="profit" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Expense Breakdown */}
          <Paper sx={{ p: 3, flex: '1 1 300px', minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Expense Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={expenseData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="amount"
                  label={({ category, percentage }: any) => `${category} ${percentage}%`}
                >
                  {expenseData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </Paper>
        </Box>

        {/* Second Charts Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Cash Flow */}
          <Paper sx={{ p: 3, flex: '1 1 400px', minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Cash Flow Analysis
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={financialData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} />
                <Area type="monotone" dataKey="cashFlow" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>

          {/* Project Performance */}
          <Paper sx={{ p: 3, flex: '1 1 400px', minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Project Financial Performance
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={projectData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="project" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="budgeted" fill="#8884d8" name="Budgeted" />
                <Bar dataKey="actual" fill="#82ca9d" name="Actual" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>

        {/* Project Details Table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Project Financial Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell align="right">Budgeted</TableCell>
                  <TableCell align="right">Actual</TableCell>
                  <TableCell align="right">Variance</TableCell>
                  <TableCell align="right">Completion</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectData.map((project) => (
                  <TableRow key={project.project}>
                    <TableCell>{project.project}</TableCell>
                    <TableCell align="right">{formatCurrency(project.budgeted)}</TableCell>
                    <TableCell align="right">{formatCurrency(project.actual)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        color={project.variance > 0 ? 'error.main' : 'success.main'}
                      >
                        {formatCurrency(Math.abs(project.variance))}
                        {project.variance > 0 ? ' over' : ' under'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{project.completion}%</TableCell>
                    <TableCell>
                      <Chip
                        label={project.variance > 0 ? 'Over Budget' : 'On Track'}
                        color={project.variance > 0 ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default FinancialReports;